import { useEffect, useRef, useState } from 'preact/hooks';
import { UserProfileIcon, CurrentUserProfileIcon } from '@components/avatars/user-profile-icon';
import { ReadonlyMinidoc } from '@components/minidoc/readonly-minidoc';
import { timeago, fmtFullTime } from 'shared/dateutil';
import { MIRASEE_STAFF_TITLES, AIP_COURSE_ID } from 'shared/mirasee-consts';
import { IcoClock, IcoChat } from '@components/icons';
import { rpx } from 'client/lib/rpx-client';
import { useCurrentUser } from 'client/lib/auth';
import { showError } from '@components/app-error';
import { useDisposableMemo } from 'client/lib/hooks';
import {
  cardMiddleware,
  defaultToolbarActions,
  minidoc,
  minidocToolbar,
  placeholder,
} from 'minidoc-editor';
import { useMinidoc } from '@components/minidoc';
import { EditorWrapper } from '@components/minidoc/minidoc-root';
import { useIntl } from 'shared/intl/use-intl';
import { generateMediaCard, mediaMiddleware } from '@components/media-card';
import { embedHtmlCard } from '@components/raw-html-card';
import { toolbarDropdown } from '@components/toolbar-dropdown';
import { ALL_MEMBERS_ID, Mentionable } from '@course/components/discussions/mention-picker';
import { MiraseeBadge } from '@components/mirasee-badge';
import { isImage } from 'shared/media';
import { useConfiguration } from '@components/router/session-context';
import { imageUrl } from 'client/utils/cdn';
import type { CommunityDiscussion } from './types';

function mountEditorEl(el: Element) {
  return (wrapper: HTMLDivElement | null) => {
    if (wrapper && !wrapper.contains(el)) {
      wrapper.appendChild(el);
    }
  };
}

function isAutoGeneratedTitle(prompt: string, description: string) {
  const bodyText = description
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trimStart();
  const titleText = prompt.replace(/…$/, '');
  return bodyText.startsWith(titleText);
}

function PinToggleButton({ isPinned, onClick }: { isPinned: boolean; onClick: () => void }) {
  return (
    <button
      class={`ml-2 text-gray-300 hover:text-indigo-500 transition ${isPinned ? 'text-indigo-400 hover:text-gray-400' : ''}`}
      onClick={onClick}
      title={isPinned ? 'Unpin from top' : 'Pin to top'}
    >
      <svg
        fill="currentColor"
        viewBox="0 0 24 24"
        style="width: 16px; height: 16px; flex-shrink: 0"
      >
        <path d="M17.1 3.3c-.4-.4-1-.4-1.4 0l-3.5 3.5-4-1.3c-.4-.1-.9 0-1.2.3L5.3 7.5c-.4.4-.4 1 0 1.4l3.5 3.5-4.5 4.5c-.2.2-.3.4-.3.7v1.1c0 .6.4 1 1 1h1.1c.3 0 .5-.1.7-.3l4.5-4.5 3.5 3.5c.4.4 1 .4 1.4 0l1.7-1.7c.3-.3.4-.7.3-1.2l-1.3-4 3.5-3.5c.4-.4.4-1 0-1.4l-2.3-2.3z" />
      </svg>
    </button>
  );
}

function PinnedIcon() {
  return (
    <svg fill="currentColor" viewBox="0 0 24 24" style="width: 18px; height: 18px; flex-shrink: 0">
      <path d="M17.1 3.3c-.4-.4-1-.4-1.4 0l-3.5 3.5-4-1.3c-.4-.1-.9 0-1.2.3L5.3 7.5c-.4.4-.4 1 0 1.4l3.5 3.5-4.5 4.5c-.2.2-.3.4-.3.7v1.1c0 .6.4 1 1 1h1.1c.3 0 .5-.1.7-.3l4.5-4.5 3.5 3.5c.4.4 1 .4 1.4 0l1.7-1.7c.3-.3.4-.7.3-1.2l-1.3-4 3.5-3.5c.4-.4.4-1 0-1.4l-2.3-2.3z" />
    </svg>
  );
}

function HideToggleButton({ isHidden, onClick }: { isHidden: boolean; onClick: () => void }) {
  return (
    <button
      class={`ml-1 text-gray-300 hover:text-gray-500 transition ${isHidden ? 'text-gray-400 hover:text-gray-600' : ''}`}
      onClick={onClick}
      title={isHidden ? 'Show in feed' : 'Hide from feed'}
      aria-label={isHidden ? 'Show in feed' : 'Hide from feed'}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        style="width: 16px; height: 16px; flex-shrink: 0"
      >
        {isHidden ? (
          <>
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </>
        ) : (
          <>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </>
        )}
      </svg>
    </button>
  );
}

function PostMenu({
  onEdit,
  canHide,
  isHidden,
  isPinned,
  onHide,
}: {
  onEdit?: () => void;
  canHide?: boolean;
  isHidden?: boolean;
  isPinned?: boolean;
  onHide?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  return (
    <div class="relative shrink-0" ref={menuRef}>
      <button
        class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
        onClick={() => setIsOpen(!isOpen)}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" style="width: 20px; height: 20px">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {isOpen && (
        <div class="absolute right-0 top-full mt-1 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-10 min-w-[160px]">
          {onEdit && (
            <button
              class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition"
              onClick={() => {
                setIsOpen(false);
                onEdit();
              }}
            >
              Edit
            </button>
          )}
          {canHide && !isPinned && onHide && (
            <button
              class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition"
              onClick={() => {
                setIsOpen(false);
                onHide();
              }}
            >
              {isHidden ? 'Show in feed' : 'Hide from feed'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function InlinePostEditor({
  discussion,
  onSave,
  onCancel,
}: {
  discussion: CommunityDiscussion;
  onSave: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(discussion.prompt || '');
  const [isSaving, setIsSaving] = useState(false);

  const editor = useMinidoc({
    doc: discussion.description || '',
    middleware: (intl) => [
      placeholder("What's on your mind?"),
      minidocToolbar([
        ...defaultToolbarActions.filter(
          (x) => x.id === 'bold' || x.id === 'italic' || x.id === 'link',
        ),
        toolbarDropdown({ intl, mediaOnly: true, showVimeo: true }),
      ]),
      cardMiddleware([generateMediaCard({}), embedHtmlCard]),
      mediaMiddleware(),
    ],
    onChange() {},
  });

  async function handleSave() {
    const description = editor.serialize();
    if (!description.trim() && !title.trim()) {
      return;
    }
    setIsSaving(true);
    try {
      const rawText = title.trim() || editor.root.textContent?.trim() || '';
      const prompt =
        rawText.length <= 80
          ? rawText || 'Untitled'
          : rawText.slice(0, 80).replace(/\s+\S*$/, '') + '…';
      const mentionEls = Array.from(editor.root.querySelectorAll<HTMLElement>('a[data-user-id]'));
      const mentions = [
        ...new Set(mentionEls.map((x) => x.dataset.userId || '').filter((x) => !!x)),
      ];
      const hasAllMention = mentions.includes(ALL_MEMBERS_ID);
      await rpx.discussions.upsertDiscussion({
        id: discussion.id,
        courseId: AIP_COURSE_ID,
        prompt,
        description,
        mentions: hasAllMention ? [] : mentions,
        hasAllMention,
      });
      onSave();
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div class="px-5 pt-4 pb-2">
      <input
        type="text"
        class="w-full text-xl font-bold text-gray-900 dark:text-white placeholder-gray-400 border-none focus:ring-0 focus:outline-none p-0 mb-2 bg-transparent"
        placeholder="Discussion title"
        value={title}
        onInput={(e: any) => setTitle(e.target.value)}
      />
      <div class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-indigo-400 transition">
        <div class="relative">
          <Mentionable courseId={AIP_COURSE_ID} editor={editor} />
          <EditorWrapper editor={editor} class="py-2 px-3 min-h-[80px] text-sm" />
        </div>
        <div class="border-t border-gray-100 dark:border-gray-600 px-3 py-2">
          <div ref={mountEditorEl(editor.toolbar.root)} class="flex items-center gap-0.5" />
        </div>
      </div>
      <div class="flex items-center justify-end gap-2 mt-3">
        <button
          class="px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          class="inline-flex items-center justify-center rounded-full px-5 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition disabled:opacity-50"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export function PostCard({
  discussion,
  onCommentAdded,
  onPostEdited,
  canPin,
  onPinToggled,
  canHide,
  onHideToggled,
  defaultBodyExpanded,
}: {
  discussion: CommunityDiscussion;
  onCommentAdded?: (discussionId: string) => void;
  onPostEdited?: () => void;
  canPin?: boolean;
  onPinToggled?: () => void;
  canHide?: boolean;
  onHideToggled?: () => void;
  defaultBodyExpanded?: boolean;
}) {
  const currentUser = useCurrentUser();
  const { author, recentComments, numComments } = discussion;
  const isMirasee = discussion.authorLevel === 'guide' || discussion.authorLevel === 'facilitator';
  const staffTitle = MIRASEE_STAFF_TITLES[author.id];
  const discussionUrl = `/courses/${AIP_COURSE_ID}/discussions/all/${discussion.id}`;
  const isScheduled = discussion.scheduledAt && new Date(discussion.scheduledAt) > new Date();
  const [isLiked, setIsLiked] = useState(discussion.isLiked);
  const [likeCount, setLikeCount] = useState(discussion.likeCount);
  const [isEditing, setIsEditing] = useState(false);
  const [isBodyExpanded, setIsBodyExpanded] = useState(defaultBodyExpanded ?? false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const isMyPost = currentUser?.id === discussion.userId;

  useEffect(() => {
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (el && el.scrollHeight > el.clientHeight) {
        setIsTruncated(true);
      }
    });
  }, [discussion.description]);

  async function handleHideToggle() {
    try {
      await rpx.miraseeCommunity.toggleCommunityHide({
        discussionId: discussion.id,
        isHidden: !discussion.isHidden,
      });
      onHideToggled?.();
    } catch (err) {
      showError(err);
    }
  }

  async function handlePinToggle() {
    try {
      await rpx.miraseeCommunity.toggleCommunityPin({
        discussionId: discussion.id,
        isPinned: !discussion.isPinned,
      });
      onPinToggled?.();
    } catch (err) {
      showError(err);
    }
  }

  async function handleDiscussionLike() {
    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    setLikeCount((c) => c + (wasLiked ? -1 : 1));
    try {
      await rpx.miraseeCommunity.toggleDiscussionLike({
        discussionId: discussion.id,
        isLiked: !wasLiked,
      });
    } catch (err) {
      setIsLiked(wasLiked);
      setLikeCount((c) => c + (wasLiked ? 1 : -1));
      showError(err);
    }
  }

  return (
    <article
      class={`rounded-2xl mb-4 ${
        isScheduled
          ? 'bg-amber-50/60 dark:bg-amber-950/30 ring-1 ring-amber-200/60 dark:ring-amber-800/40'
          : discussion.isPinned
            ? 'bg-indigo-50/50 dark:bg-indigo-950/30 ring-1 ring-indigo-100/60 dark:ring-indigo-800/40'
            : 'bg-indigo-50/40 dark:bg-gray-800/60'
      } ${discussion.isHidden ? 'opacity-50' : ''}`}
    >
      {discussion.isHidden && (
        <div class="flex items-center gap-2 px-5 pt-3 text-sm text-gray-400 dark:text-gray-500">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            style="width: 14px; height: 14px"
          >
            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
          <span class="font-medium">Hidden from feed</span>
        </div>
      )}
      {isScheduled && (
        <div class="flex items-center gap-2 px-5 pt-3 text-sm text-amber-700 dark:text-amber-400">
          <IcoClock class="w-4 h-4" />
          <span class="font-medium">
            Scheduled for {fmtFullTime(new Date(discussion.scheduledAt!))}
          </span>
        </div>
      )}
      <div class="px-5 pt-5 pb-0">
        <div class="flex items-start gap-3">
          <UserProfileIcon user={author} size="w-12 h-12 text-sm" maxWidth={96} />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-bold text-base text-gray-900 dark:text-white">{author.name}</span>
              {isMirasee && <MiraseeBadge />}
              <span class="text-sm text-gray-400 dark:text-gray-500">
                {timeago(discussion.createdAt, ' ago')}
                {discussion.category && (
                  <a
                    href={`/courses/${AIP_COURSE_ID}/discussions/${discussion.category.id}`}
                    class="text-gray-400 dark:text-gray-500 no-underline hover:text-gray-600 dark:hover:text-gray-300 transition"
                  >
                    {' · in '}
                    {discussion.category.title}
                  </a>
                )}
              </span>
              {canPin ? (
                <PinToggleButton isPinned={!!discussion.isPinned} onClick={handlePinToggle} />
              ) : discussion.isPinned ? (
                <span class="ml-auto text-gray-300" title="Pinned">
                  <PinnedIcon />
                </span>
              ) : undefined}
              {canHide && !discussion.isPinned && (
                <HideToggleButton isHidden={!!discussion.isHidden} onClick={handleHideToggle} />
              )}
            </div>
            {staffTitle && <p class="text-sm text-gray-500 leading-tight">{staffTitle}</p>}
          </div>
          {(isMyPost || canHide) && !isEditing && (
            <PostMenu
              onEdit={isMyPost ? () => setIsEditing(true) : undefined}
              canHide={canHide}
              isHidden={!!discussion.isHidden}
              isPinned={!!discussion.isPinned}
              onHide={handleHideToggle}
            />
          )}
        </div>
      </div>

      {isEditing ? (
        <InlinePostEditor
          discussion={discussion}
          onSave={() => {
            setIsEditing(false);
            onPostEdited?.();
          }}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <div class="px-5 pt-4 pb-2 community-prose">
          {discussion.prompt &&
            !(
              discussion.description &&
              isAutoGeneratedTitle(discussion.prompt, discussion.description)
            ) && (
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                <a
                  href={discussionUrl}
                  class="no-underline text-inherit hover:text-indigo-700 dark:hover:text-indigo-400 transition-colors"
                >
                  {discussion.prompt}
                </a>
              </h3>
            )}
          {discussion.description && (
            <div>
              <div
                ref={bodyRef}
                class={`relative ${isBodyExpanded ? '' : 'max-h-96 overflow-hidden'}`}
              >
                <ReadonlyMinidoc
                  content={discussion.description}
                  class="max-w-none"
                  enableLinkPreviews
                />
                {isTruncated && !isBodyExpanded && (
                  <div
                    class="absolute bottom-0 left-0 right-0 z-10 pointer-events-none"
                    style="height: 80px; background: linear-gradient(to top, #f0f3ff, transparent)"
                  />
                )}
              </div>
              {isTruncated && !isBodyExpanded && (
                <div class="flex justify-center mt-2">
                  <button
                    class="inline-flex items-center rounded-full px-5 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                    onClick={() => setIsBodyExpanded(true)}
                  >
                    Read more
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div class="mx-5 flex items-center border-t border-b border-gray-200/50 dark:border-gray-700/50 py-2">
        <button
          class={`inline-flex items-center gap-1.5 text-sm transition ${isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
          onClick={handleDiscussionLike}
        >
          <svg
            viewBox="0 0 24 24"
            fill={isLiked ? 'currentColor' : 'none'}
            stroke={isLiked ? 'none' : 'currentColor'}
            stroke-width="1.5"
            style="width: 18px; height: 18px"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
            />
          </svg>
          {likeCount > 0 && <span class="text-xs font-medium">{likeCount}</span>}
        </button>
        <span class="mx-3 text-gray-200 dark:text-gray-600">·</span>
        <span class="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500">
          <IcoChat class="w-4 h-4" />
          {numComments === 0
            ? 'No comments yet'
            : numComments === 1
              ? '1 comment'
              : `${numComments} comments`}
        </span>
        <a
          href={discussionUrl}
          class="ml-auto text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 no-underline transition"
        >
          View Discussion &rarr;
        </a>
      </div>

      <InlineComments
        discussionId={discussion.id}
        recentComments={recentComments}
        onCommentAdded={onCommentAdded}
        isCurrentUserStaff={!!canPin}
      />
    </article>
  );
}

type OptimisticReply = {
  id: string;
  content: string;
  createdAt: string;
  isStaff?: boolean;
  author: { id: string; name: string; profilePhotoUrl?: string };
};

function InlineCommentEditor({
  commentId,
  discussionId,
  initialContent,
  onSaved,
  onCancel,
}: {
  commentId: string;
  discussionId: string;
  initialContent: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const intl = useIntl();

  const editEditor = useDisposableMemo(() => {
    return minidoc({
      doc: initialContent,
      middleware: [
        placeholder('Edit comment...'),
        minidocToolbar([
          ...defaultToolbarActions.filter(
            (x) => x.id === 'bold' || x.id === 'italic' || x.id === 'link',
          ),
          toolbarDropdown({ intl, mediaOnly: true, showVimeo: true }),
        ]),
        cardMiddleware([generateMediaCard({}), embedHtmlCard]),
        mediaMiddleware(),
      ],
    });
  }, []);

  async function handleSave() {
    const content = editEditor.serialize();
    const textContent = editEditor.root.textContent?.trim() || '';
    const hasMedia = editEditor.root.querySelectorAll('mini-card').length > 0;
    if (!textContent && !hasMedia) {
      return;
    }
    setIsSaving(true);
    try {
      const mentionEls = Array.from(
        editEditor.root.querySelectorAll<HTMLElement>('a[data-user-id]'),
      );
      const mentions = [
        ...new Set(mentionEls.map((x) => x.dataset.userId || '').filter((x) => !!x)),
      ];
      const hasAllMention = mentions.includes(ALL_MEMBERS_ID);
      await rpx.comments.saveComment({
        id: commentId,
        discussionId,
        content,
        files: [],
        mentions: hasAllMention ? [] : mentions,
        hasAllMention,
      });
      onSaved();
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div class="mt-1">
      <div class="relative bg-white dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-indigo-400 transition">
        <Mentionable courseId={AIP_COURSE_ID} editor={editEditor} />
        <div
          ref={mountEditorEl(editEditor.root)}
          class="flex-1 px-4 py-2 text-sm border-none focus:ring-0 focus:outline-none rounded-2xl bg-transparent dark:text-white inline-comment-editor cursor-text"
          onClick={() => editEditor.root.focus()}
        />
        <div class="flex items-center justify-between border-t border-gray-100 dark:border-gray-600 px-3 py-1.5">
          <div ref={mountEditorEl(editEditor.toolbar.root)} class="flex items-center" />
          <div class="flex items-center gap-2">
            <button
              class="text-xs font-semibold text-gray-400 hover:text-gray-600 transition"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              class="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition disabled:opacity-50"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineComments({
  discussionId,
  recentComments: initialComments,
  onCommentAdded,
  isCurrentUserStaff,
}: {
  discussionId: string;
  recentComments: CommunityDiscussion['recentComments'];
  onCommentAdded?: (discussionId: string) => void;
  isCurrentUserStaff?: boolean;
}) {
  const currentUser = useCurrentUser();
  const config = useConfiguration();
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [comments, setComments] = useState(initialComments);
  const [editingCommentId, setEditingCommentId] = useState<string | undefined>();
  const [replyingTo, setReplyingTo] = useState<string | undefined>();
  const [commentPosted, setCommentPosted] = useState(false);
  const [optimisticReplies, setOptimisticReplies] = useState<Record<string, OptimisticReply[]>>({});
  const [isSavingReply, setIsSavingReply] = useState(false);
  const isSavingReplyRef = useRef(false);
  const intl = useIntl();

  function fileUrl(path: string) {
    if (path.includes('://')) {
      return path;
    }
    return config ? imageUrl(config, path) : path;
  }

  const editor = useDisposableMemo(() => {
    return minidoc({
      doc: '',
      middleware: [
        placeholder('Write a comment...'),
        minidocToolbar([
          ...defaultToolbarActions.filter(
            (x) => x.id === 'bold' || x.id === 'italic' || x.id === 'link',
          ),
          toolbarDropdown({ intl, mediaOnly: true, showVimeo: true }),
        ]),
        cardMiddleware([generateMediaCard({}), embedHtmlCard]),
        mediaMiddleware(),
      ],
    });
  }, []);

  const replyEditor = useDisposableMemo(() => {
    return minidoc({
      doc: '',
      middleware: [
        placeholder('Reply...'),
        minidocToolbar([
          ...defaultToolbarActions.filter(
            (x) => x.id === 'bold' || x.id === 'italic' || x.id === 'link',
          ),
          toolbarDropdown({ intl, mediaOnly: true, showVimeo: true }),
        ]),
        cardMiddleware([generateMediaCard({}), embedHtmlCard]),
        mediaMiddleware(),
      ],
    });
  }, []);

  useEffect(() => {
    setComments(initialComments);
    setOptimisticReplies({});
  }, [initialComments]);

  async function toggleLike(commentId: string, currentlyLiked: boolean) {
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId
          ? { ...c, isLiked: !currentlyLiked, likeCount: c.likeCount + (currentlyLiked ? -1 : 1) }
          : c,
      ),
    );
    try {
      await rpx.comments.toggleCommentLike({ id: commentId, isLiked: !currentlyLiked });
    } catch (err) {
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, isLiked: currentlyLiked, likeCount: c.likeCount + (currentlyLiked ? 1 : -1) }
            : c,
        ),
      );
      showError(err);
    }
  }

  async function submitReply() {
    const content = editor.serialize();
    const textContent = editor.root.textContent?.trim() || '';
    const hasMedia = editor.root.querySelectorAll('mini-card').length > 0;
    if ((!textContent && !hasMedia) || isSavingRef.current) {
      return;
    }
    setIsSaving(true);
    isSavingRef.current = true;

    const mentionEls = Array.from(editor.root.querySelectorAll<HTMLElement>('a[data-user-id]'));
    const mentions = [...new Set(mentionEls.map((x) => x.dataset.userId || '').filter((x) => !!x))];
    const hasAllMention = mentions.includes(ALL_MEMBERS_ID);

    const optimisticComment = currentUser
      ? {
          id: `optimistic-${Date.now()}`,
          content,
          createdAt: new Date().toISOString(),
          isLiked: false,
          likeCount: 0,
          authorLevel: isCurrentUserStaff ? ('guide' as const) : undefined,
          files: [] as Array<{ id: string; type: string; url: string; name: string }>,
          author: {
            id: currentUser.id,
            name: currentUser.name,
            profilePhotoUrl: currentUser.profilePhotoUrl,
          },
        }
      : undefined;
    if (optimisticComment) {
      setComments((prev) => [...prev, optimisticComment]);
    }
    editor.root.innerHTML = '<p><br></p>';
    try {
      const saved = await rpx.comments.saveComment({
        discussionId,
        content,
        files: [],
        mentions: hasAllMention ? [] : mentions,
        hasAllMention,
      });
      if (optimisticComment && saved) {
        setComments((prev) =>
          prev.map((c) => (c.id === optimisticComment.id ? { ...c, id: saved.id } : c)),
        );
      }
      setCommentPosted(true);
    } catch (err) {
      if (optimisticComment) {
        setComments((prev) => prev.filter((c) => c.id !== optimisticComment.id));
      }
      showError(err);
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  }

  async function submitThreadReply() {
    if (!replyingTo) {
      return;
    }
    const content = replyEditor.serialize();
    const textContent = replyEditor.root.textContent?.trim() || '';
    const hasMedia = replyEditor.root.querySelectorAll('mini-card').length > 0;
    if ((!textContent && !hasMedia) || isSavingReplyRef.current) {
      return;
    }
    setIsSavingReply(true);
    isSavingReplyRef.current = true;

    const mentionEls = Array.from(
      replyEditor.root.querySelectorAll<HTMLElement>('a[data-user-id]'),
    );
    const mentions = [...new Set(mentionEls.map((x) => x.dataset.userId || '').filter((x) => !!x))];
    const hasAllMention = mentions.includes(ALL_MEMBERS_ID);
    const parentId = replyingTo;

    const optimisticReply = currentUser
      ? {
          id: `optimistic-${Date.now()}`,
          content,
          createdAt: new Date().toISOString(),
          isStaff: isCurrentUserStaff,
          author: {
            id: currentUser.id,
            name: currentUser.name,
            profilePhotoUrl: currentUser.profilePhotoUrl,
          },
        }
      : undefined;

    if (optimisticReply) {
      setOptimisticReplies((prev) => ({
        ...prev,
        [parentId]: [...(prev[parentId] || []), optimisticReply],
      }));
    }

    replyEditor.root.innerHTML = '<p><br></p>';
    setReplyingTo(undefined);

    try {
      const saved = await rpx.comments.saveComment({
        discussionId,
        parentId,
        content,
        files: [],
        mentions: hasAllMention ? [] : mentions,
        hasAllMention,
      });
      if (optimisticReply && saved) {
        setOptimisticReplies((prev) => ({
          ...prev,
          [parentId]: (prev[parentId] || []).map((r) =>
            r.id === optimisticReply.id ? { ...r, id: saved.id } : r,
          ),
        }));
      }
    } catch (err) {
      if (optimisticReply) {
        setOptimisticReplies((prev) => ({
          ...prev,
          [parentId]: (prev[parentId] || []).filter((r) => r.id !== optimisticReply.id),
        }));
      }
      showError(err);
    } finally {
      setIsSavingReply(false);
      isSavingReplyRef.current = false;
    }
  }

  function handleReplyClick(commentId: string) {
    if (replyingTo === commentId) {
      setReplyingTo(undefined);
    } else {
      setReplyingTo(commentId);
      replyEditor.root.innerHTML = '<p><br></p>';
    }
  }

  useEffect(() => {
    if (replyingTo) {
      setTimeout(() => {
        replyEditor.root.focus();
      });
    }
  }, [replyingTo]);

  const rootComments = comments.filter((c) => !c.parentId);
  const repliesByParent: Record<string, typeof comments> = {};
  for (const c of comments) {
    if (c.parentId) {
      if (!repliesByParent[c.parentId]) {
        repliesByParent[c.parentId] = [];
      }
      repliesByParent[c.parentId].push(c);
    }
  }

  return (
    <div class="px-5 pb-4 pt-3">
      {rootComments.length > 0 && (
        <div class="flex flex-col gap-3 pb-3">
          {rootComments.map((comment) => {
            const serverReplies = repliesByParent[comment.id] || [];
            const localReplies = optimisticReplies[comment.id] || [];
            return (
              <div key={comment.id}>
                <div
                  class={`flex gap-2.5 ${comment.id.startsWith('optimistic-') ? 'comment-highlight' : ''}`}
                >
                  <UserProfileIcon
                    user={comment.author}
                    size="w-8 h-8 text-[10px] shrink-0"
                    maxWidth={64}
                  />
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="font-semibold text-sm text-gray-900 dark:text-white">
                        {comment.author.name}
                      </span>
                      {(comment.authorLevel === 'guide' ||
                        comment.authorLevel === 'facilitator') && <MiraseeBadge size="xs" />}
                      <span class="text-xs text-gray-400">
                        {timeago(comment.createdAt, ' ago')}
                      </span>
                    </div>
                    {editingCommentId === comment.id ? (
                      <InlineCommentEditor
                        commentId={comment.id}
                        discussionId={discussionId}
                        initialContent={comment.content || ''}
                        onSaved={() => {
                          setEditingCommentId(undefined);
                          onCommentAdded?.(discussionId);
                        }}
                        onCancel={() => setEditingCommentId(undefined)}
                      />
                    ) : (
                      <>
                        {comment.content && (
                          <div class="text-sm text-gray-700 dark:text-gray-300 community-prose mt-0.5">
                            <ReadonlyMinidoc
                              content={comment.content}
                              class="prose-sm max-w-none"
                              enableLinkPreviews
                            />
                          </div>
                        )}
                        {comment.files?.length > 0 && (
                          <div class="flex flex-col items-start gap-2 mt-2">
                            {comment.files.map((file) =>
                              isImage(file.type) ? (
                                <img
                                  key={file.id}
                                  src={fileUrl(file.url)}
                                  alt={file.name}
                                  class="rounded-lg max-h-64 max-w-full object-contain"
                                  loading="lazy"
                                />
                              ) : (
                                <a
                                  key={file.id}
                                  href={fileUrl(file.url)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="text-sm text-indigo-600 hover:text-indigo-700 underline"
                                >
                                  {file.name}
                                </a>
                              ),
                            )}
                          </div>
                        )}
                        <div class="flex items-center gap-3 mt-1">
                          {!comment.id.startsWith('optimistic-') && (
                            <>
                              <button
                                class={`inline-flex items-center gap-1 text-xs font-semibold transition ${comment.isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                                onClick={() => toggleLike(comment.id, comment.isLiked)}
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  fill={comment.isLiked ? 'currentColor' : 'none'}
                                  stroke={comment.isLiked ? 'none' : 'currentColor'}
                                  stroke-width="2"
                                  style="width: 14px; height: 14px"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                                  />
                                </svg>
                                {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
                              </button>
                              <button
                                class="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-gray-600 transition"
                                onClick={() => handleReplyClick(comment.id)}
                              >
                                <svg
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="1.5"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  style="width: 13px; height: 13px"
                                >
                                  <path d="M3 3.5c0 4 1.5 6.5 7 6.5" />
                                  <path d="M7.5 7l3 3-3 3" />
                                </svg>
                                Reply
                              </button>
                              {currentUser?.id === comment.author.id && (
                                <button
                                  class="text-xs text-gray-300 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-300 transition"
                                  onClick={() => setEditingCommentId(comment.id)}
                                >
                                  Edit
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {serverReplies.map((reply) => (
                  <div key={reply.id} class="flex gap-2 ml-10 mt-2">
                    <UserProfileIcon
                      user={reply.author}
                      size="w-6 h-6 text-[8px] shrink-0"
                      maxWidth={48}
                    />
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="font-semibold text-xs text-gray-900 dark:text-white">
                          {reply.author.name}
                        </span>
                        {(reply.authorLevel === 'guide' || reply.authorLevel === 'facilitator') && (
                          <MiraseeBadge size="xs" />
                        )}
                        <span class="text-xs text-gray-400">
                          {timeago(reply.createdAt, ' ago')}
                        </span>
                        {editingCommentId !== reply.id && currentUser?.id === reply.author.id && (
                          <button
                            class="text-xs text-gray-300 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-300 transition"
                            onClick={() => setEditingCommentId(reply.id)}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                      {editingCommentId === reply.id ? (
                        <InlineCommentEditor
                          commentId={reply.id}
                          discussionId={discussionId}
                          initialContent={reply.content || ''}
                          onSaved={() => {
                            setEditingCommentId(undefined);
                            onCommentAdded?.(discussionId);
                          }}
                          onCancel={() => setEditingCommentId(undefined)}
                        />
                      ) : (
                        <>
                          {reply.content && (
                            <div class="text-sm text-gray-700 dark:text-gray-300 community-prose mt-0.5">
                              <ReadonlyMinidoc
                                content={reply.content}
                                class="prose-sm max-w-none"
                                enableLinkPreviews
                              />
                            </div>
                          )}
                          {reply.files?.length > 0 && (
                            <div class="flex flex-col items-start gap-2 mt-2">
                              {reply.files.map((file) =>
                                isImage(file.type) ? (
                                  <img
                                    key={file.id}
                                    src={fileUrl(file.url)}
                                    alt={file.name}
                                    class="rounded-lg max-h-64 max-w-full object-contain"
                                    loading="lazy"
                                  />
                                ) : (
                                  <a
                                    key={file.id}
                                    href={fileUrl(file.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-sm text-indigo-600 hover:text-indigo-700 underline"
                                  >
                                    {file.name}
                                  </a>
                                ),
                              )}
                            </div>
                          )}
                          <div class="flex items-center gap-3 mt-1">
                            {!reply.id.startsWith('optimistic-') && (
                              <button
                                class="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-gray-600 transition"
                                onClick={() => handleReplyClick(comment.id)}
                              >
                                <svg
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="1.5"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  style="width: 13px; height: 13px"
                                >
                                  <path d="M3 3.5c0 4 1.5 6.5 7 6.5" />
                                  <path d="M7.5 7l3 3-3 3" />
                                </svg>
                                Reply
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                {localReplies.map((reply) => (
                  <div
                    key={reply.id}
                    class={`flex gap-2 ml-10 mt-2 ${reply.id.startsWith('optimistic-') ? 'comment-highlight' : ''}`}
                  >
                    <UserProfileIcon
                      user={reply.author}
                      size="w-6 h-6 text-[8px] shrink-0"
                      maxWidth={48}
                    />
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="font-semibold text-xs text-gray-900 dark:text-white">
                          {reply.author.name}
                        </span>
                        {reply.isStaff && <MiraseeBadge size="xs" />}
                        <span class="text-xs text-gray-400">just now</span>
                      </div>
                      {reply.content && (
                        <div class="text-sm text-gray-700 dark:text-gray-300 community-prose mt-0.5">
                          <ReadonlyMinidoc
                            content={reply.content}
                            class="prose-sm max-w-none"
                            enableLinkPreviews
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {replyingTo === comment.id && (
                  <div class="ml-10 mt-3 flex gap-2.5">
                    <CurrentUserProfileIcon size="w-6 h-6 text-[8px] shrink-0 mt-1.5" />
                    <div class="flex-1 relative bg-white dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-indigo-400 transition">
                      <Mentionable courseId={AIP_COURSE_ID} editor={replyEditor} />
                      <div
                        ref={mountEditorEl(replyEditor.root)}
                        class="px-4 py-2.5 text-sm border-none focus:ring-0 focus:outline-none bg-transparent dark:text-white inline-comment-editor cursor-text"
                        onClick={() => replyEditor.root.focus()}
                      />
                      <div class="flex items-center justify-between px-3 py-2">
                        <div
                          ref={mountEditorEl(replyEditor.toolbar.root)}
                          class="flex items-center"
                        />
                        <div class="flex items-center gap-2">
                          <button
                            class="text-xs font-medium text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                            onClick={() => setReplyingTo(undefined)}
                          >
                            Cancel
                          </button>
                          <button
                            class="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition disabled:opacity-50"
                            onClick={submitThreadReply}
                            disabled={isSavingReply}
                          >
                            {isSavingReply ? 'Posting...' : 'Reply'}
                            <svg
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              style="width: 10px; height: 10px"
                            >
                              <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!replyingTo && !commentPosted && (
        <div class="flex gap-2.5">
          <CurrentUserProfileIcon size="w-8 h-8 text-[10px] shrink-0 mt-1.5" />
          <div class="flex-1 relative bg-white dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-indigo-400 transition">
            <Mentionable courseId={AIP_COURSE_ID} editor={editor} />
            <div
              ref={mountEditorEl(editor.root)}
              class="px-4 py-2.5 text-sm border-none focus:ring-0 focus:outline-none bg-transparent dark:text-white inline-comment-editor cursor-text"
              onClick={() => editor.root.focus()}
            />
            <div class="flex items-center justify-between px-3 py-2">
              <div ref={mountEditorEl(editor.toolbar.root)} class="flex items-center" />
              <button
                class="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition disabled:opacity-50"
                onClick={submitReply}
                disabled={isSaving}
              >
                {isSaving ? 'Posting...' : 'Post'}
                <svg viewBox="0 0 20 20" fill="currentColor" style="width: 11px; height: 11px">
                  <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {!replyingTo && commentPosted && (
        <div class="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 py-1">
          <span>Comment posted</span>
          <span class="text-gray-300 dark:text-gray-600">&middot;</span>
          <a
            href={`/courses/${AIP_COURSE_ID}/discussions/all/${discussionId}`}
            class="font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 no-underline transition"
          >
            View discussion &rarr;
          </a>
          <span class="text-gray-300 dark:text-gray-600">&middot;</span>
          <button
            class="font-medium text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
            onClick={() => setCommentPosted(false)}
          >
            Write another
          </button>
        </div>
      )}
    </div>
  );
}
